'use client';

import React from 'react';
import type * as Runtime from './responsive-dialog-runtime';
import { deferOverlayComponent } from './dialog';
import { usePathname } from 'next/navigation';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';

// Resolve the whole module before mounting Root so Content, Title and focusable siblings
// use the same loaded implementation synchronously (no nested lazy mount gaps).
let loadedRuntime: typeof Runtime | undefined;
function loadRuntime() {
  return import('./responsive-dialog-runtime').then(runtime => {
    loadedRuntime = runtime;
    return runtime;
  });
}

export const ResponsiveDialog = deferOverlayComponent<typeof Runtime.ResponsiveDialog>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialog),
  'ResponsiveDialog',
  () => loadedRuntime?.ResponsiveDialog,
);

export const ResponsiveDialogContent = deferOverlayComponent<typeof Runtime.ResponsiveDialogContent>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogContent),
  'ResponsiveDialogContent',
  () => loadedRuntime?.ResponsiveDialogContent,
);

export const ResponsiveDialogOverlay = deferOverlayComponent<typeof Runtime.ResponsiveDialogOverlay>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogOverlay),
  'ResponsiveDialogOverlay',
  () => loadedRuntime?.ResponsiveDialogOverlay,
);

export const ResponsiveDialogHeader = deferOverlayComponent<typeof Runtime.ResponsiveDialogHeader>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogHeader),
  'ResponsiveDialogHeader',
  () => loadedRuntime?.ResponsiveDialogHeader,
);

export const ResponsiveDialogFooter = deferOverlayComponent<typeof Runtime.ResponsiveDialogFooter>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogFooter),
  'ResponsiveDialogFooter',
  () => loadedRuntime?.ResponsiveDialogFooter,
);

export const ResponsiveDialogTitle = deferOverlayComponent<typeof Runtime.ResponsiveDialogTitle>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogTitle),
  'ResponsiveDialogTitle',
  () => loadedRuntime?.ResponsiveDialogTitle,
);

export const ResponsiveDialogDescription = deferOverlayComponent<typeof Runtime.ResponsiveDialogDescription>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogDescription),
  'ResponsiveDialogDescription',
  () => loadedRuntime?.ResponsiveDialogDescription,
);

export const ResponsiveDialogTrigger = deferOverlayComponent<typeof Runtime.ResponsiveDialogTrigger>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogTrigger),
  'ResponsiveDialogTrigger',
  () => loadedRuntime?.ResponsiveDialogTrigger,
);

export const ResponsiveDialogClose = deferOverlayComponent<typeof Runtime.ResponsiveDialogClose>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogClose),
  'ResponsiveDialogClose',
  () => loadedRuntime?.ResponsiveDialogClose,
);

export const ResponsiveDialogPortal = deferOverlayComponent<typeof Runtime.ResponsiveDialogPortal>(
  () => loadRuntime().then(runtime => runtime.ResponsiveDialogPortal),
  'ResponsiveDialogPortal',
  () => loadedRuntime?.ResponsiveDialogPortal,
);

type DialogConfig = {
  id: string;
  content: React.ReactNode;
  onClose?: () => void;
};

type DialogStoreState = {
  dialogs: Record<string, DialogConfig>;
};

type DialogStoreActions = {
  open: (config: DialogConfig) => void;
  close: (id: string) => void;
  closeAll: () => void;
};

const [ResponsiveDialogContextProvider, useResponsiveDialogStore] = createContextStore<
  DialogStoreState,
  DialogStoreActions
>(
  {
    dialogs: {},
  },
  set => ({
    open: (config: DialogConfig) =>
      set(state => ({
        dialogs: {
          ...state.dialogs,
          [config.id]: config,
        },
      })),
    close: (id: string) =>
      set(state => {
        const dialogs = { ...state.dialogs };
        delete dialogs[id];
        return { dialogs };
      }),
    closeAll: () =>
      set(() => ({
        dialogs: {},
      })),
  }),
);

export function ResponsiveDialogProvider(props: React.PropsWithChildren) {
  return (
    <ResponsiveDialogContextProvider>
      <ResponsiveDialogProviderRenderer />
      {props.children}
    </ResponsiveDialogContextProvider>
  );
}

function ResponsiveDialogProviderRenderer() {
  const dialogs = useResponsiveDialogStore(s => s.dialogs);
  const close = useResponsiveDialogStore(s => s.close);
  const closeAll = useResponsiveDialogStore(s => s.closeAll);

  const pathname = usePathname();

  React.useEffect(() => {
    closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      {Object.values(dialogs).map(dialog => (
        <ResponsiveDialog
          key={dialog.id}
          open
          onOpenChange={open => {
            if (!open) {
              dialog.onClose?.();
              close(dialog.id);
            }
          }}
        >
          {dialog.content}
        </ResponsiveDialog>
      ))}
    </>
  );
}

export { useResponsiveDialogStore };
export type { DialogConfig };
